import { AppBar, Badge, Button, Dialog, DialogActions, DialogContent, DialogContentText, Divider, IconButton, Menu, MenuItem, Popover, Tooltip } from "@mui/material"
import { ReactComponent as CompanyIcon } from "./app-icon.svg"
import React, { useEffect, useState } from "react"
import { AccountCircle, Mail } from "@mui/icons-material"
import SettingsIcon from "@mui/icons-material/Settings"
import { Box } from "@mui/system"
import { useAppDispatch, useAppSelector } from "../../store/hooks"
import { fetchInvitations } from "../../network/network"
import { Invitation } from "../../models/invitation"
import InvitationListView from "../invitations/InvitationListView"
import { loadProjcetsSilently, loadProjects } from "../../store/project/projectSlice"
import ProfileView from "../profile/ProfileView"
import { Link, useLocation } from "react-router-dom"
import ProjectSettingsView from "../projects/ProjectSettingsView"
import PlacementSpecificationsView from "../placements/specifications/PlacementSpecificationsView"
import AlertContainerView from "../placements/specifications/alerts/AlertContainerView"
import Placement, { convertToPlacementEntry, isPlacementEntryEqual, PlacementEntry } from "../../models/placement"

interface Props {
  onSignOut: () => void
  isMobile?: boolean
}

const AppbarView = (props: Props) => {
  const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(null)
  const [invitationsMenuAnchorElement, setInvitationsMenuAnchorElement] = useState<null | HTMLElement>(null)
  const [projectSettingsAnchor, setProjectSettingsAnchor] = useState<null | HTMLElement>(null)
  const [placementSettingsAnchor, setPlacementSettingsAnchor] = useState<null | HTMLElement>(null)
  const [specsDialogOpen, setSpecsDialogOpen] = useState(false)
  const [alertsDialogOpen, setAlertsDialogOpen] = useState(false)
  const [specsChanged, setSpecsChanged] = useState(false)
  const [warningDialogOpen, setWarningDialogOpen] = useState(false)

  const accessToken = useAppSelector(state => state.user.token)
  const loadedProjects = useAppSelector(state => state.project.projects)
  const dispatch = useAppDispatch()
  const location = useLocation()

  const [invitations, setInvitations] = useState<Invitation[]>([])

  useEffect(() => {
    if (!accessToken) {
      return
    }
    const loadInvitations = async () => {
      const result = await fetchInvitations(accessToken)
      setInvitations(result)
    }
    loadInvitations()
  }, [])

  const projectIdMatch = location.pathname.match(/^\/projects\/(\d+)$/)
  const placementIdMatch = location.pathname.match(/^\/projects\/\d+\/placements\/(\d+)$/)
  const selectedProject = projectIdMatch
    ? loadedProjects.find(p => p.id === parseInt(projectIdMatch[1]))
    : undefined
  const selectedPlacement: Placement | undefined = placementIdMatch && selectedProject
    ? selectedProject.placements?.find(p => p.id === parseInt(placementIdMatch[1]))
    : undefined

  const showPlacementSettings = Boolean(props.isMobile && selectedPlacement && selectedProject)
  const showProjectSettings = Boolean(props.isMobile && selectedProject) && !showPlacementSettings
  const showDefaultButtons = !showProjectSettings && !showPlacementSettings

  const handleSpecsChange = (entry: PlacementEntry) => {
    if (!selectedPlacement) {
      return
    }
    setSpecsChanged(!isPlacementEntryEqual(entry, convertToPlacementEntry(selectedPlacement)))
  }

  const openSpecsDialog = () => {
    setSpecsChanged(false)
    setPlacementSettingsAnchor(null)
    setSpecsDialogOpen(true)
  }

  const openAlertsDialog = () => {
    setPlacementSettingsAnchor(null)
    setAlertsDialogOpen(true)
  }

  const closeSpecsDialog = () => {
    if (!specsChanged) {
      setSpecsDialogOpen(false)
    } else {
      setWarningDialogOpen(true)
    }
  }

  const handlePlacementUpdate = (placement: Placement) => {
    if (!selectedProject) {
      return
    }
    const updatedPlacements = selectedProject.placements.map(p => p.id === placement.id ? placement : p)
    const updatedProject = { ...selectedProject, placements: updatedPlacements }
    const updatedProjects = loadedProjects.map(p => p.id === selectedProject.id ? updatedProject : p)
    dispatch(loadProjcetsSilently(updatedProjects))
  }

  const settingsIconButtonSx = {
    marginRight: "8px"
  }

  return (
    <AppBar
      color="inherit"
      position="fixed"
      elevation={1}
      sx={{
        padding: "8px 0px",
        zIndex: (theme) => theme.zIndex.drawer + 1
      }}>
      <Box flexDirection="row" justifyContent="space-between" display="flex" alignItems="center">
        <Link to="/">
          <CompanyIcon style={{
            height: "40px"
          }} />
        </Link>
        <Box>
          {showProjectSettings && selectedProject &&
            <Tooltip title="Project Settings">
              <IconButton
                edge="end"
                color="primary"
                sx={settingsIconButtonSx}
                aria-label="Project Settings"
                onClick={(event) => setProjectSettingsAnchor(event.currentTarget)}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          }
          {showPlacementSettings && selectedPlacement && selectedProject &&
            <Tooltip title="Placement Settings">
              <span>
                <IconButton
                  edge="end"
                  color="primary"
                  sx={settingsIconButtonSx}
                  aria-label="Placement Settings"
                  disabled={selectedProject.userInfo.permission === "readonly"}
                  onClick={(event) => setPlacementSettingsAnchor(event.currentTarget)}
                >
                  <SettingsIcon />
                </IconButton>
              </span>
            </Tooltip>
          }
          {showDefaultButtons &&
            <>
              <IconButton
                edge="end"
                disabled={invitations.length == 0}
                color="primary"
                sx={{
                  marginRight: "8px"
                }}
                onClick={(event) => {
                  setInvitationsMenuAnchorElement(event.currentTarget)
                }}
              >
                <Badge badgeContent={invitations.length} color="error">
                  <Mail />
                </Badge>
              </IconButton>
              <IconButton
                edge="end"
                sx={{
                  marginRight: "8px"
                }}
                aria-controls="user-menu"
                aria-haspopup="true"
                color="primary"
                onClick={ (event: React.MouseEvent<HTMLElement>) => {
                  setAnchorElement(event.currentTarget)
                }}
              >
                <AccountCircle />
              </IconButton>
            </>
          }
          <Menu
            id="user-menu"
            sx={{
              padding: 2
            }}
            anchorEl={anchorElement}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right"
            }}
            keepMounted
            transformOrigin={{
              vertical: "top",
              horizontal: "right"
            }}
            open={Boolean(anchorElement)}
            onClose={() => {
              setAnchorElement(null)
            }}
          >
            <ProfileView onSignOut={props.onSignOut} />
          </Menu>
          <Popover
            id="invitations-menu"
            anchorEl={invitationsMenuAnchorElement}
            PaperProps={{
              elevation: 0,
            }}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right"
            }}
            keepMounted
            transformOrigin={{
              vertical: "top",
              horizontal: "right"
            }}
            open={Boolean(invitationsMenuAnchorElement) && invitations.length > 0}
            onClose={() => {
              setInvitationsMenuAnchorElement(null)
            }}
          >
            <InvitationListView
              invitations={invitations}
              didRespondToInvitation={(invitation: Invitation) => {
                setInvitations(invitations.filter((i) => i.id !== invitation.id))
                if (accessToken) {
                  dispatch(loadProjects(accessToken))
                }
              }}
            />
          </Popover>
          {selectedProject &&
            <Popover
              id="project-settings-popover"
              anchorEl={projectSettingsAnchor}
              open={Boolean(projectSettingsAnchor)}
              onClose={() => setProjectSettingsAnchor(null)}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right"
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right"
              }}
            >
              <Box sx={{ width: "500px" }}>
                <ProjectSettingsView project={selectedProject} />
              </Box>
            </Popover>
          }
          {selectedPlacement && selectedProject &&
            <>
              <Menu
                anchorEl={placementSettingsAnchor}
                open={Boolean(placementSettingsAnchor)}
                onClose={() => setPlacementSettingsAnchor(null)}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right"
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right"
                }}
              >
                <MenuItem onClick={openSpecsDialog}>Specifications</MenuItem>
                <Divider variant="middle" component="li" />
                <MenuItem onClick={openAlertsDialog}>Alerts</MenuItem>
              </Menu>
              <Dialog
                open={specsDialogOpen}
                onClose={closeSpecsDialog}
                maxWidth="xs"
                fullWidth={true}
              >
                <PlacementSpecificationsView
                  placement={selectedPlacement}
                  project={selectedProject}
                  onPlacementUpdate={(p) => {
                    handlePlacementUpdate(p)
                    setSpecsDialogOpen(false)
                  }}
                  onClose={closeSpecsDialog}
                  onEntryChange={handleSpecsChange}
                />
              </Dialog>
              <Dialog
                open={warningDialogOpen}
                onClose={() => setWarningDialogOpen(false)}
              >
                <DialogContent>
                  <DialogContentText>
                    You have unsaved changes that will be lost. Are you sure you want to cancel?
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button variant="outlined" onClick={() => {
                    setSpecsDialogOpen(false)
                    setWarningDialogOpen(false)
                  }}>Yes</Button>
                  <Button variant="contained" onClick={() => {
                    setWarningDialogOpen(false)
                  }} autoFocus>
                    Go back
                  </Button>
                </DialogActions>
              </Dialog>
              <Dialog
                sx={{
                  "& .MuiDialog-container": {
                    alignItems: "flex-start"
                  },
                  "& .MuiDialog-paper": {
                    maxWidth: "400px"
                  }
                }}
                maxWidth="sm"
                fullWidth={false}
                scroll="paper"
                open={alertsDialogOpen}
                onClose={() => setAlertsDialogOpen(false)}
              >
                <AlertContainerView
                  projectId={selectedProject.id}
                  placementId={selectedPlacement.id}
                  onBack={() => setAlertsDialogOpen(false)}
                />
              </Dialog>
            </>
          }
        </Box>
      </Box>
    </AppBar>
  )
}

export default AppbarView
